import { fallbackLocale, regionDefaultLanguage, regions } from "./settings"

export function mapWebLangToServerLang(webLanguage: string): string {
	if (webLanguage === "en") {
		return "en-us"
	}
	return webLanguage
}

// 从URL路径中提取语言代码
export function getLanguageFromPath(pathname: string): string {
	// 假设URL格式为 /{locale}/... 或 /{language}/...
	const pathSegments = pathname.split("/").filter(Boolean)
	if (pathSegments.length === 0) {
		return fallbackLocale
	}

	const firstSegment = pathSegments[0]

	// 如果是纯语言代码，直接返回
	if (firstSegment.includes("-")) {
		return firstSegment
	}

	// 如果是地区代码，需要根据constant.ts的映射转换为语言
	return mapRegionToLanguage(firstSegment)
}

// 将地区代码映射到默认语言（基于constant.ts的映射）
export function mapRegionToLanguage(region: string): string {
	const regionLanguageMap: Record<string, string> = {
		hk: "zh-hant",
		mo: "zh-hant",
		tw: "zh-hant",
		gb: "en-us",
		us: "en-us",
		cn: "zh-hans",
		jp: "ja-jp",
		kr: "ko-kr",
		sg: "en-us",
		au: "en-us",
		ca: "en-us",
		de: "de-de",
		fr: "fr-fr",
		it: "it-it",
		es: "es-es",
		ru: "ru-ru",
		in: "hi-in",
		br: "pt-br",
		mx: "es-mx",
		th: "th-th",
		vn: "vi-vn",
		id: "id-id",
		my: "ms-my",
	}

	return regionLanguageMap[region.toLowerCase()] || fallbackLocale
}

// 构建新的URL路径（保持地区不变，只切换语言）
// export function buildNewPath(
// 	currentPathname: string,
// 	newLanguage: string,
// ): string {
// 	const pathSegments = currentPathname.split("/").filter(Boolean)
// 	if (pathSegments.length === 0) {
// 		return `/${newLanguage}`
// 	}

// 	const firstSegment = pathSegments[0]

// 	// 如果第一个段是地区代码，需要转换为对应的语言
// 	if (!firstSegment.includes("-")) {
// 		// 这是地区代码，需要根据新语言和地区映射来决定URL格式
// 		const region = firstSegment
// 		const regionLanguageMap = {
// 			hk: "zh-hant",
// 			mo: "zh-hant",
// 			tw: "zh-hant",
// 		}

// 		// 如果新语言是该地区的默认语言，使用地区代码
// 		if (newLanguage === regionLanguageMap[region]) {
// 			return `/${region}/${pathSegments.slice(1).join("/")}`
// 		} else {
// 			// 否则使用语言-地区组合
// 			return `/${newLanguage}-${region.toUpperCase()}/${pathSegments.slice(1).join("/")}`
// 		}
// 	} else {
// 		// 第一个段已经是语言代码，直接替换
// 		return `/${newLanguage}/${pathSegments.slice(1).join("/")}`
// 	}
// }

// 检查是否是有效的语言代码
export function isValidLanguage(language: string): boolean {
	// 这里可以根据实际支持的语言列表进行验证
	const supportedLanguages = [
		"en-us",
		"zh-hant",
		"zh-hans",
		"ja-jp",
		"ko-kr",
		"de-de",
		"fr-fr",
		"it-it",
		"es-es",
		"ru-ru",
		"hi-in",
		"pt-br",
		"th-th",
		"vi-vn",
		"id-id",
		"ms-my",
	]
	return supportedLanguages.includes(language.toLowerCase())
}

// 从组合locale获取语言代码
export function getLanguageFromLocale(locale: string): string {
	if (locale.includes("-")) {
		// 处理语言-地区组合，如 "zh-hant-HK" 或 "en-us-HK"
		const parts = locale.split("-")

		// 检查是否是语言-地区组合（最后一部分是地区代码）
		const lastPart = parts[parts.length - 1]
		if (regions.includes(lastPart.toLowerCase() as any)) {
			// 这是语言-地区组合，提取语言部分
			if (parts[0] === "zh" && parts[1] === "hant") {
				return "zh-hant"
			}
			if (parts[0] === "zh" && parts[1] === "hans") {
				return "zh-hans"
			}
			// 其他语言取前两部分
			return parts.slice(0, 2).join("-")
		} else {
			// 这可能只是语言代码，如 "zh-hant"
			if (parts[0] === "zh" && parts[1] === "hant") {
				return "zh-hant"
			}
			if (parts[0] === "zh" && parts[1] === "hans") {
				return "zh-hans"
			}
			// 其他语言取前两部分
			return parts.slice(0, 2).join("-")
		}
	}

	// 如果是纯地区代码，映射到默认语言
	return mapRegionToLanguage(locale)
}

// 将constant.ts格式的语言代码转换为小写格式
export function normalizeLanguageCode(languageCode: string): string {
	return languageCode.toLowerCase()
}

// 将小写格式的语言代码转换为constant.ts格式
export function denormalizeLanguageCode(languageCode: string): string {
	const parts = languageCode.split("-")
	if (parts.length >= 2) {
		// 语言部分统一输出为小写，地区部分保持大写（符合URL规范）
		const language = parts[0].toLowerCase()
		const region = parts[1].toUpperCase()
		return `${language}-${region}`
	}
	return languageCode
}

// 向后兼容的函数导出
export function getAreaFromLocale(locale: string): string {
	if (locale.includes("-")) {
		// 处理语言-地区组合，如 "zh-hant-HK" 或 "en-us-HK"
		const parts = locale.split("-")
		const lastPart = parts[parts.length - 1]

		// 检查最后一部分是否是地区代码
		if (regions.includes(lastPart.toLowerCase() as any)) {
			return lastPart.toLowerCase()
		}

		// 如果不是地区代码，可能是纯语言代码，返回默认地区
		return "hk" // 默认地区
	}

	// 如果是纯地区代码，直接返回
	return locale.toLowerCase()
}

export function buildLocaleString(
	language: string,
	region: string,
	availableLanguages?: string[],
): string {
	// 如果提供了可用语言列表，使用第一个作为该地区的默认语言
	if (availableLanguages && availableLanguages.length > 0) {
		const regionDefault = availableLanguages[0]
		if (language === regionDefault) {
			// 默认语言只显示地区码
			return region.toLowerCase()
		}
		// 非默认语言显示语言-地区组合，地区大写
		return `${language}-${region.toUpperCase()}`
	}

	// 如果没有提供可用语言列表，回退到硬编码的映射（保持向后兼容）
	const regionDefault =
		regionDefaultLanguage[region as keyof typeof regionDefaultLanguage]
	if (language === regionDefault) {
		// 默认语言只显示地区码
		return region.toLowerCase()
	}

	// 非默认语言显示语言-地区组合，地区大写
	return `${language}-${region.toUpperCase()}`
}

export function isValidLocale(locale: string): boolean {
	// 检查是否是有效的语言代码
	if (isValidLanguage(locale)) {
		return true
	}

	// 检查是否是有效的地区代码
	if (regions.includes(locale.toLowerCase() as any)) {
		return true
	}

	// 检查是否是语言-地区组合
	if (locale.includes("-")) {
		const parts = locale.split("-")
		const language = parts.slice(0, -1).join("-")
		const region = parts[parts.length - 1]

		// 确保语言和地区都有效
		const isLanguageValid = isValidLanguage(language)
		const isRegionValid = regions.includes(region.toLowerCase() as any)

		return isLanguageValid && isRegionValid
	}

	return false
}

export function isRegion(locale: string): boolean {
	return regions.includes(locale.toLowerCase() as any)
}

export function findStandardLocale(locale: string): string | undefined {
	if (isValidLocale(locale)) {
		return locale
	}
	return undefined
}

export function capitalizeRegion(region: string): string {
	return region?.toUpperCase() || ""
}

export function mapWebAreaToServerArea(webArea: string): string {
	// 统一地区码为小写
	return webArea.toLowerCase()
}
